export type TOffsetType = "spreading" | "compaction";

/**
 * レイヤ面の向き (CONTEXT.md「レイヤモード」)。既定は天面平行。
 * client 内の実行時パラメータで、サーバには送らず保存もしない (ADR 0006):
 * 水平モードは実行時に `--base` へ渡す天面を水平面に差し替えるだけ。
 */
export const layerModes = {
	PARALLEL: "parallel",
	HORIZONTAL: "horizontal",
} as const;
export type TLayerMode = (typeof layerModes)[keyof typeof layerModes];
export const DEFAULT_LAYER_MODE: TLayerMode = layerModes.PARALLEL;

/**
 * 転圧外周列 (CONTEXT.md)。1 層 = 1 本の閉列で、穴・飛び地はバイナリ側で橋渡し済み。
 * `points` は Local 座標 (m) の flat [x,y,z,...]。始点が終点に繰り返される
 */
export interface ICompactionOutline {
	/** mm */
	readonly offset: number;
	readonly points: ReadonlyArray<number>;
}

/** Matches viewer's ISpreadingCompactionInput (defined locally to avoid stale d.ts) */
export interface ISpreadingCompactionInput {
	readonly surfaces: ReadonlyArray<{
		readonly type: TOffsetType;
		readonly offset: number;
		readonly geometry: {
			readonly vertices: number[];
			readonly indices: number[];
		};
	}>;
}

export type ITarget = IRoadTarget | IFlatGroundTarget | IDesignTarget;

export interface IRoadTarget {
	type: "road";
	id: string;
	name: string;
}

export interface IFlatGroundTarget {
	type: "flatground";
	id: string;
	name: string;
}

export interface IDesignTarget {
	type: "design";
	/** Numeric asset ID used as viewer key */
	id: number;
	name: string;
}

export type ITerrain = IPointCloudTerrain;

export interface IPointCloudTerrain {
	type: "pointcloud";
	id: number;
	name: string;
}

export type IExecution =
	| { status: "idle" }
	| { status: "processing" }
	| {
			status: "success";
			result: ISpreadingCompactionInput;
			/** 旧レコード (lib < 0.5.0) は空配列。CSV 出力可否は `hasCompactionOutline` で判定 */
			compactionOutline: ReadonlyArray<ICompactionOutline>;
	  }
	| { status: "error"; error: string };

export interface IOffsetVisibility {
	type: TOffsetType;
	offset: number;
	visible: boolean;
}

/** Persisted store entry. */
export interface ISpreadingCompaction {
	id: number;
	name: string;
	target: ITarget | null;
	terrain: ITerrain | null;
	compactionSurfaceSpacing: number;
	/** Slope shoulder offset in mm */
	slopeShoulderOffset: number;
	layerMode: TLayerMode;
	execution: IExecution;
	visible: boolean;
	targetVisible: boolean;
	targetOpacity: number;
	offsetVisibility: IOffsetVisibility[];
}

/**
 * Draft form state for a not-yet-persisted entry.
 * Derived from `ISpreadingCompaction` minus post-execute fields so adding
 * a new field to the persisted shape doesn't silently miss the draft.
 */
export type ISpreadingCompactionDraft = Omit<
	ISpreadingCompaction,
	"id" | "execution" | "visible" | "offsetVisibility"
>;

export interface ISpreadingCompactionState {
	spreadingCompactions: ISpreadingCompaction[];
	draft: ISpreadingCompactionDraft | null;
	selectedId: number | null;
	isActive: boolean;
	/**
	 * A draft has no `execution`, so a per-entry status cannot express "running"
	 * for the first, not-yet-saved execution.
	 */
	isExecuting: boolean;
	/** Same reason as `isExecuting`: a draft has nowhere to record a failure. */
	draftError: string | null;
}
